/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Component, Injector, Input, ViewChild } from '@angular/core';
import { ButtonService, DetailAbstract, UNPLACEHOLDER, UtilService } from '@dm/core';
import { LicenseKey } from 'app/common/constants/license-key';
import { type NFSShare } from 'app/common/types/mo';
import { ProductConfig, SWITCH } from 'app/config/switch';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { FileSystemEntryE } from '../../../file-system/create-file-system/create-file-system.component';
import { AddExtensionFilteringComponent } from '../../extension-filtering/add-extension-filtering/add-extension-filtering.component';
import { ExtensionFilteringComponent } from '../../extension-filtering/extension-filtering.component';
import { ShareMoTypeE } from '../../extension-filtering/extension-filtering-str.service';
import { AddClientNfsShareComponent } from '../client-nfs-share/add-client-nfs-share/add-client-nfs-share.component';
import { ClientNfsShareComponent } from '../client-nfs-share/client-nfs-share.component';
import { CreateNfsShareComponent } from '../create-nfs-share/create-nfs-share.component';
import { DeleteNfsShareComponent } from '../delete-nfs-share/delete-nfs-share.component';
import { ModifyNfsShareComponent } from '../modify-nfs-share/modify-nfs-share.component';
import { NfsShareStrService } from '../nfs-share-str.service';
import { NfsShareService } from './../nfs-share.service';

@Component({
    selector: 'app-detail-nfs-share',
    templateUrl: './detail-nfs-share.component.html',
    styleUrls: ['./detail-nfs-share.component.scss'],
})
export class DetailNfsShareComponent extends DetailAbstract<NFSShare> {
    @Input() entry: FileSystemEntryE;
    /**
     * 文件系统/dtree详情页传入
     */
    @Input() fileSystem?: FileSystem;
    @ViewChild('clientDt') clientDt: ClientNfsShareComponent;
    @ViewChild('extensionFilteringDt') extensionFilteringDt: ExtensionFilteringComponent;

    i18n = this.str.i18n;
    UNPLACEHOLDER = UNPLACEHOLDER;
    ProductConfig = ProductConfig;
    activeTab: string = this.i18n.title.permissionList;
    charEncodingType = this.str.charEncodingType;
    enableShowSnapOpts = this.str.enableShowSnapOpts;
    ShareMoTypeE = ShareMoTypeE;
    SWITCH = SWITCH;

    isSimpleMode = SWITCH.IS_SIMPLE_MODE;

    constructor(
        injector: Injector,
        public str: NfsShareStrService,
        private buttonService: ButtonService,
        private nfsService: NfsShareService,
        public utilService: UtilService,
    ) {
        super(injector);
    }

    initOperationBtns(): void {
        this.operationBtns = [
            this.buttonService.create({
                id: 'create-as-detial-nfs-share',
                label: this.i18n.btn.createAsBtn,
                permission: ['share_W'],
                license: [LicenseKey.NAS],
                isShow: () => !SWITCH.IS_SIMPLE_MODE,
                disabledCheckWithTips: [
                    {
                        tip: this.i18n.tip.gnsCreateNfsTip,
                        checker: () => this.data.FSID === UNPLACEHOLDER,
                    },
                ],
                command: () => {
                    this.create();
                },
            }),
            this.buttonService.create({
                id: 'add-client-detial-nfs-share',
                label: this.i18n.btn.addClient,
                theme: 'primary',
                permission: ['share_W'],
                depPermission: ['task_W'],
                disabledCheckWithTips: [
                    {
                        tip: this.i18n.tip.gnsCreateNfsTip,
                        checker: () => this.data.FSID === UNPLACEHOLDER,
                    },
                ],
                command: () => {
                    this.addClient();
                },
            }),
            this.buttonService.create({
                id: 'add-extension-filerting',
                permission: ['share_W'],
                label: this.i18n.btn.addExtensionFilteringBtn,
                isShow: () => !SWITCH.IS_SIMPLE_MODE && !ProductConfig.isHideNfsFileFiltering,
                disabledCheckWithTips: [
                    {
                        tip: this.i18n.tip.gnsCreateNfsTip,
                        checker: () => this.data.FSID === UNPLACEHOLDER,
                    },
                ],
                command: () => {
                    this.addExtensionFiltering();
                },
            }),
            { separator: true },
            this.buttonService.create({
                id: 'modify-nfs-share',
                label: this.i18n.btn.modifyBtn,
                permission: ['share_W'],
                disabledCheckWithTips: [
                    {
                        tip: this.i18n.tip.gnsCreateNfsTip,
                        checker: () => this.data.FSID === UNPLACEHOLDER,
                    },
                ],
                command: () => {
                    this.modify();
                },
            }),
            this.buttonService.create({
                id: 'delete-nfs-share',
                label: this.i18n.btn.deleteBtn,
                permission: ['share_W'],
                command: () => {
                    this.delete();
                },
            }),
        ];
    }

    /**
     * 获取NFS共享
     */
    getData(): Observable<any> {
        return this.nfsService.get(this.data.ID, this.data.vstoreId)
            .pipe(
                map(({ data }) => data),
            );
    }

    /**
     * 修改NFS共享
     */
    modify(): void {
        this.doAction(ModifyNfsShareComponent, { data: this.data });
    }

    /**
     * 删除NFS共享
     */
    delete(): void {
        this.doAction(DeleteNfsShareComponent, { dataList: [this.data] });
    }

    create(): void {
        // 如果传入了文件系统，就传文件系统本身的租户ID
        const { vstoreId, vstoreName } = this.data;
        this.doAction(CreateNfsShareComponent, {
            vstoreId,
            vstoreName,
            createAsShare: this.data,
            supportSmartGui: false,
        });
    }

    addClient(): void {
        this.doAction(AddClientNfsShareComponent, {
            nfsShare: this.data,
            entry: this.entry,
            fileSystem: this.fileSystem,
            onClose: (closeArgs) => {
                // 如果创建完成，刷新表格
                if (closeArgs.refresh) {
                    if (this.clientDt) {
                        this.clientDt.refresh();
                    }
                }
            },
        });
    }

    addExtensionFiltering(): void {
        this.doAction(AddExtensionFilteringComponent, {
            share: this.data,
            shareType: ShareMoTypeE.NFS,
            onClose: (closeArgs) => {
                // 如果创建完成，刷新表格
                if (closeArgs.refresh) {
                    if (this.extensionFilteringDt) {
                        this.extensionFilteringDt.refresh();
                    }
                }
            },
        });
    }
}
